import React, { useMemo } from 'react';
import { Client, Order, OrderStatus, OutfitType } from '../types';

interface DashboardProps {
    clients: Client[];
    orders: Order[];
    onClose: () => void;
}

const OUTFIT_TYPE_LABELS: Record<string, string> = {
    Haut: 'Hauts', Bas: 'Bas', Robe: 'Robes', Jupe: 'Jupes', Manteau: 'Manteaux',
    Gilet: 'Gilets', Combinaison: 'Combinaisons', 'Kaftan / Boubou': 'Kaftans',
    'Corset / Bustier': 'Corsets', Complet: 'Complets', 'Tenue Traditionnelle': 'Traditionnels',
};

const STATUS_COLORS: Record<OrderStatus, string> = {
    pending: '#F59E0B', in_progress: '#3B82F6', ready: '#6366F1', delivered: '#10B981', cancelled: '#EF4444',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
    pending: 'En attente', in_progress: 'En cours', ready: 'Prêt', delivered: 'Livré', cancelled: 'Annulé',
};

export const Dashboard: React.FC<DashboardProps> = ({ clients, orders, onClose }) => {
    const stats = useMemo(() => {
        const now = new Date();
        const thisMonth = now.getMonth();
        const thisYear = now.getFullYear();
        const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const activeClients = clients.filter(c => !c.isDeleted);
        const newClientsThisMonth = activeClients.filter(c => {
            const updated = new Date(c.updatedAt);
            return updated.getMonth() === thisMonth && updated.getFullYear() === thisYear;
        });
        const allMeasurements = activeClients.flatMap(c => c.measurements?.filter(m => !m.isDeleted) || []);
        const measurementsByType: Record<string, number> = {};
        allMeasurements.forEach(m => {
            const type = m.outfitType || 'Autre';
            measurementsByType[type] = (measurementsByType[type] || 0) + 1;
        });
        const activeOrders = orders.filter(o => !o.isDeleted);
        const ordersByStatus: Record<OrderStatus, number> = { pending: 0, in_progress: 0, ready: 0, delivered: 0, cancelled: 0 };
        activeOrders.forEach(o => { ordersByStatus[o.status] = (ordersByStatus[o.status] || 0) + 1; });
        const overdueOrders = activeOrders.filter(o => {
            if (!o.dueDate || o.status === 'delivered' || o.status === 'cancelled') return false;
            return new Date(o.dueDate) < now;
        });
        const upcomingOrders = activeOrders.filter(o => {
            if (!o.dueDate || o.status === 'delivered' || o.status === 'cancelled') return false;
            const due = new Date(o.dueDate);
            return due >= now && due <= oneWeekFromNow;
        });
        const totalRevenue = activeOrders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + (o.negotiatedAmount || 0), 0);
        const paidRevenue = activeOrders.filter(o => o.paid && o.status !== 'cancelled').reduce((sum, o) => sum + (o.negotiatedAmount || 0), 0);
        const unpaidRevenue = totalRevenue - paidRevenue;
        const collectionRate = totalRevenue > 0 ? Math.round((paidRevenue / totalRevenue) * 100) : 100;
        const unpaidOrdersCount = activeOrders.filter(o => !o.paid && o.status !== 'cancelled').length;
        const clientsEvolution: { month: string; count: number }[] = [];
        for (let i = 5; i >= 0; i--) {
            const date = new Date(thisYear, thisMonth - i, 1);
            const monthName = date.toLocaleDateString('fr-FR', { month: 'short' });
            const clientsUpToMonth = activeClients.filter(c => {
                const updated = new Date(c.updatedAt);
                return updated <= new Date(date.getFullYear(), date.getMonth() + 1, 0);
            }).length;
            clientsEvolution.push({ month: monthName, count: clientsUpToMonth });
        }
        return {
            totalClients: activeClients.length, newClientsThisMonth: newClientsThisMonth.length,
            totalMeasurements: allMeasurements.length, measurementsByType, ordersByStatus,
            totalOrders: activeOrders.length, overdueOrders: overdueOrders.length,
            upcomingOrders: upcomingOrders.length, totalRevenue, paidRevenue, unpaidRevenue,
            collectionRate, unpaidOrdersCount, clientsEvolution,
        };
    }, [clients, orders]);

    const maxClients = Math.max(...stats.clientsEvolution.map(e => e.count), 1);

    return (
        <div className="min-h-screen t-page pb-24">
            {/* Header */}
            <div className="sticky top-0 z-20 t-header px-4 py-4 shadow-sm">
                <div className="max-w-5xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-xl shadow-sm">📊</div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Tableau de Bord</h1>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 rounded-xl transition-all border border-gray-200 dark:border-white/10"
                    >
                        Fermer
                    </button>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
                {/* Cartes KPI */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Clients */}
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xl">👥</div>
                            <span className="text-sm font-medium text-gray-500 dark:text-slate-400">Clients</span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{stats.totalClients}</p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            +{stats.newClientsThisMonth} ce mois
                        </p>
                    </div>

                    {/* Mesures */}
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-xl">📐</div>
                            <span className="text-sm font-medium text-gray-500 dark:text-slate-400">Mesures</span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{stats.totalMeasurements}</p>
                        <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">{Object.keys(stats.measurementsByType).length} types enregistrés</p>
                    </div>

                    {/* Commandes */}
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/10 rounded-full -mr-8 -mt-8 blur-2xl group-hover:bg-amber-400/20 transition-all" />
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-xl">📋</div>
                            <span className="text-sm font-medium text-gray-500 dark:text-slate-400">Commandes</span>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{stats.totalOrders}</p>
                        <div className="mt-2 space-y-1">
                            {stats.overdueOrders > 0 && (
                                <p className="text-[10px] text-rose-600 dark:text-rose-400 flex items-center gap-1 font-bold bg-rose-50 dark:bg-rose-500/10 px-2 py-0.5 rounded-full w-fit border border-rose-100 dark:border-rose-500/20">
                                    {stats.overdueOrders} en retard
                                </p>
                            )}
                            {stats.unpaidOrdersCount > 0 ? (
                                <p className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1 font-bold bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 rounded-full w-fit border border-amber-100 dark:border-amber-500/20">
                                    {stats.unpaidOrdersCount} impayées
                                </p>
                            ) : (
                                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium italic">
                                    Tout est soldé ✨
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Revenus */}
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-400/10 rounded-full -mr-8 -mt-8 blur-2xl group-hover:bg-emerald-400/20 transition-all" />
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-xl">💰</div>
                            <span className="text-sm font-medium text-gray-500 dark:text-slate-400">Encaissements</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            {stats.paidRevenue.toLocaleString('fr-FR')} <span className="text-[10px] uppercase text-gray-400 dark:text-slate-500">FCFA</span>
                        </p>
                        <div className="mt-3">
                            <div className="flex justify-between items-center text-[10px] mb-1">
                                <span className="text-gray-400 dark:text-slate-500 uppercase tracking-tighter">Taux de collecte</span>
                                <span className="text-emerald-600 dark:text-emerald-400 font-bold">{stats.collectionRate}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-1000" style={{ width: `${stats.collectionRate}%` }} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Graphique évolution */}
                <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">📈 Évolution du Portefeuille</h2>
                        <span className="text-xs font-medium px-2 py-1 bg-gray-100 dark:bg-white/5 rounded-lg text-gray-500 dark:text-slate-400 border border-gray-200 dark:border-white/5">6 derniers mois</span>
                    </div>
                    <div className="flex items-end justify-between h-48 gap-3">
                        {stats.clientsEvolution.map((item, index) => (
                            <div key={index} className="flex-1 flex flex-col items-center group">
                                <div className="relative w-full flex justify-center mb-3">
                                    <div
                                        className="w-full max-w-16 bg-gradient-to-t from-indigo-400/40 via-indigo-500/70 to-indigo-600 rounded-2xl transition-all duration-700 hover:brightness-110"
                                        style={{ height: `${Math.max((item.count / maxClients) * 140, 12)}px` }}
                                    />
                                    <span className="absolute -top-7 text-[10px] font-bold text-gray-700 dark:text-white opacity-0 group-hover:opacity-100 transition-opacity bg-white dark:bg-slate-700 px-2 py-1 rounded-md shadow-md border border-gray-100 dark:border-white/10">
                                        {item.count}
                                    </span>
                                </div>
                                <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">{item.month}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Statuts et Mesures */}
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                            <span className="w-1.5 h-6 bg-amber-400 rounded-full" />
                            État des Commandes
                        </h2>
                        <div className="space-y-4">
                            {Object.entries(stats.ordersByStatus).map(([status, count]) => (
                                <div key={status} className="flex items-center gap-4">
                                    <div className="w-2.5 h-2.5 rounded-full ring-4 ring-gray-50 dark:ring-slate-700" style={{ backgroundColor: STATUS_COLORS[status as OrderStatus] }} />
                                    <span className="flex-1 text-sm font-medium text-gray-500 dark:text-slate-400">{STATUS_LABELS[status as OrderStatus]}</span>
                                    <span className="font-bold text-gray-800 dark:text-white bg-gray-50 dark:bg-white/5 px-3 py-1 rounded-full text-xs border border-gray-100 dark:border-white/5">{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                            <span className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                            Popularité des Modèles
                        </h2>
                        <div className="space-y-4 max-h-56 overflow-y-auto pr-2 custom-scrollbar">
                            {Object.entries(stats.measurementsByType as Record<string, number>)
                                .sort((a, b) => (b[1] as number) - (a[1] as number))
                                .slice(0, 8)
                                .map(([type, count]) => (
                                    <div key={type} className="flex flex-col gap-2">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="font-medium text-gray-500 dark:text-slate-400">{OUTFIT_TYPE_LABELS[type] || type}</span>
                                            <span className="font-bold text-gray-800 dark:text-white">{count}</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                                            <div className="h-full bg-gradient-to-r from-indigo-500 to-blue-400 rounded-full transition-all duration-1000" style={{ width: `${(count / stats.totalMeasurements) * 100}%` }} />
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>

                {/* Livraisons imminentes */}
                <div className="bg-gradient-to-br from-amber-50 dark:from-amber-900/20 via-orange-50 dark:via-orange-900/10 to-white dark:to-slate-800 rounded-3xl p-6 border border-amber-100 dark:border-amber-500/20 shadow-sm relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-8 text-6xl opacity-10 group-hover:scale-110 transition-transform duration-500">📅</div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-2xl border border-amber-200 dark:border-amber-500/20 shadow-sm">⏰</div>
                            <h2 className="text-xl font-bold text-amber-700 dark:text-amber-300">Livraisons Imminentes</h2>
                        </div>
                        <p className="text-4xl font-black text-amber-500 dark:text-amber-400 tracking-tight mb-2">
                            {stats.upcomingOrders} <span className="text-lg font-bold text-amber-400/60 uppercase tracking-widest">Commandes</span>
                        </p>
                        <p className="text-sm text-gray-500 dark:text-slate-400 font-medium italic">à livrer dans le courant de la semaine</p>
                    </div>
                </div>

                {/* Perspective Financière — carte premium (identique dans les 2 thèmes: dégradé indigo/émeraude) */}
                <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-emerald-700 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                    <div className="absolute -top-12 -right-12 w-48 h-48 bg-emerald-400/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-indigo-400/20 rounded-full blur-3xl" />

                    <h2 className="text-lg font-semibold text-white mb-10 flex items-center gap-3 relative z-10">
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-xl border border-white/20 shadow-inner">💸</div>
                        Perspective Financière
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative z-10">
                        <div className="space-y-2">
                            <p className="text-xs font-bold text-white/50 uppercase tracking-widest">Chiffre d'Affaires</p>
                            <p className="text-4xl font-black text-white">{stats.totalRevenue.toLocaleString('fr-FR')}</p>
                            <p className="text-[10px] text-white/40 font-medium">Cumul total des commandes</p>
                        </div>
                        <div className="space-y-2 md:border-l md:border-white/10 md:pl-10">
                            <p className="text-xs font-bold text-emerald-300 uppercase tracking-widest">Déjà Encaissé</p>
                            <p className="text-4xl font-black text-emerald-300">{stats.paidRevenue.toLocaleString('fr-FR')}</p>
                            <p className="text-[10px] text-emerald-300/40 font-medium italic">Liquide et disponible</p>
                        </div>
                        <div className="space-y-2 md:border-l md:border-white/10 md:pl-10">
                            <p className="text-xs font-bold text-rose-300 uppercase tracking-widest">Reste à Percevoir</p>
                            <p className="text-4xl font-black text-rose-300">{stats.unpaidRevenue.toLocaleString('fr-FR')}</p>
                            <p className="text-[10px] text-rose-300/40 font-medium italic">Créances actives</p>
                        </div>
                    </div>

                    <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between relative z-10">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Actualisation en temps réel</span>
                        </div>
                        <span className="text-[10px] font-medium text-white/30 italic">Mesurette Analytics v2.0</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
